<div class="status-page">
  <div class="title-wrapper">
    <div class="left">
      <h1>Graph Status Dashboard</h1>
      <h2>Monitor the synchronization status of our graphs</h2>
    </div>
  </div>
  
  <div class="status-content">
    <!-- Loading state -->
    <div *ngIf="loading" class="loading-state">
      <app-loader></app-loader>
      <p>Loading graph statuses...</p>
    </div>
    
    <!-- No graphs configured message -->
    <div *ngIf="!loading && (!statuses || statuses.length === 0)" class="no-graphs">
      <div class="no-graphs-content">
        <h3>No monitoring configured</h3>
        <p>No graph supervision is currently configured. Please add graph configurations to monitor their status.</p>
      </div>
    </div>
    
    <!-- Graphs status grid -->
    <div class="status-grid" *ngIf="!loading && statuses && statuses.length > 0">
      <div class="status-card" *ngFor="let status of statuses">
        <div class="card-header">
          <h3>{{ status.name }}</h3>
          <div class="status-badge" [class]="status.isHealthy ? 'healthy' : 'error'">
            {{ status.isHealthy ? '✅ Healthy' : '❌ Error' }}
          </div>
        </div>
        
        <div class="description">
          <p>{{ status.description }}</p>
          <p class="version">Version: {{ status.version }}</p>
        </div>
        
        <div class="changes">
          <h4>Changes:</h4>
          <ul>
            <li *ngFor="let change of status.changes">{{ change }}</li>
          </ul>
        </div>
        
        <div class="status-info">
          <div class="info-row">
            <span class="label">API URL:</span>
            <span class="value">{{ getShortUrl(status.url) }}</span>
          </div>
          <div class="info-row">
            <span class="label">Current Block:</span>
            <span class="value">{{ status.currentBlock | number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Web3 Block:</span>
            <span class="value">{{ (web3Service.currentBlock$ | async) | number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Sync Progress:</span>
            <div class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="getSyncProgress(status) | async"></div>
              </div>
              <span class="progress-text">{{ getSyncProgress(status) | async | number:'1.0-1' }}%</span>
            </div>
          </div>
          <div class="info-row" *ngIf="status.error">
            <span class="label">Error:</span>
            <span class="value error">{{ status.error }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Indexers Status Section -->
  <div class="indexers-section">
    <div class="section-title">
      <h2>Indexers Status</h2>
      <p>Monitor the synchronization status of sales indexers</p>
    </div>

    <!-- Loading state -->
    <div *ngIf="indexersLoading" class="loading-state">
      <app-loader></app-loader>
      <p>Loading indexer statuses...</p>
    </div>

    <!-- No indexers message -->
    <div *ngIf="!indexersLoading && (!indexers || indexers.length === 0)" class="no-graphs">
      <div class="no-graphs-content">
        <h3>No indexers found</h3>
        <p>No indexers match the configured filter pattern.</p>
      </div>
    </div>

    <!-- Indexers grid -->
    <div class="status-grid" *ngIf="!indexersLoading && indexers && indexers.length > 0">
      <div class="status-card" *ngFor="let indexer of indexers">
        <div class="card-header">
          <h3>{{ indexer.indexerName }}</h3>
          <div class="status-badge" [class]="indexer.isRunning && indexer.sync.isSynced ? 'healthy' : 'error'">
            {{ indexer.isRunning && indexer.sync.isSynced ? '✅ Synced' : indexer.isRunning ? '⚠️ Catching Up' : '❌ Stopped' }}
          </div>
        </div>

        <div class="status-info">
          <div class="info-row">
            <span class="label">Contract:</span>
            <span class="value">{{ indexer.contractAddress }}</span>
          </div>
          <div class="info-row">
            <span class="label">Status:</span>
            <span class="value">{{ indexer.status }}</span>
          </div>
          <div class="info-row">
            <span class="label">Current Block:</span>
            <span class="value">{{ indexer.blocks.current | number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Latest Block:</span>
            <span class="value">{{ indexer.blocks.latest | number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Blocks Behind:</span>
            <span class="value">{{ indexer.blocks.behind | number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Sync Status:</span>
            <span class="value">{{ indexer.sync.status }}</span>
          </div>
          <div class="info-row">
            <span class="label">Progress:</span>
            <div class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="getIndexerProgress(indexer)"></div>
              </div>
              <span class="progress-text">{{ getIndexerProgress(indexer) | number:'1.0-1' }}%</span>
            </div>
          </div>
          <div class="info-row">
            <span class="label">Total Events:</span>
            <span class="value">{{ indexer.database.totalEvents | number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Events/Hour:</span>
            <span class="value">{{ indexer.performance.eventsPerHour | number:'1.0-0' }}</span>
          </div>
          <div class="info-row" *ngIf="indexer.errors.hasErrors">
            <span class="label">Errors:</span>
            <span class="value error">{{ indexer.errors.count }} - {{ indexer.errors.lastError }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- All Bids subsection (listings indexer, any status) -->
    <div class="bids-table-section">
      <h3 class="bids-subtitle">All Bids</h3>
      <p class="bids-meta" *ngIf="lastBidFetch">Last fetch: {{ lastBidFetch | date:'medium' }}</p>

      <div *ngIf="bidsLoading && totalBidsCount === 0" class="loading-state">
        <app-loader></app-loader>
        <p>Loading bids...</p>
      </div>

      <div *ngIf="!bidsLoading && totalBidsCount === 0" class="no-graphs">
        <div class="no-graphs-content">
          <h3>No bids</h3>
          <p *ngIf="bidsHasActiveFilters">
            No bids match the current filters. Reset filters to see all bids again, or the listings indexer may be empty.
          </p>
          <p *ngIf="!bidsHasActiveFilters">
            No bids in the listings indexer or the API is unavailable.
          </p>
          <button
            *ngIf="bidsHasActiveFilters"
            type="button"
            class="bids-reset-filters-btn"
            (click)="resetBidsFilters()">
            Reset filters
          </button>
        </div>
      </div>

      <div class="bids-table-with-loader" *ngIf="totalBidsCount > 0">
        <div class="bids-loading-overlay" *ngIf="bidsLoading">
          <app-loader></app-loader>
          <p>Loading page {{ bidsCurrentPage }}...</p>
        </div>
        <app-stash-bids-table
          [bids]="bids"
          [totalCount]="totalBidsCount"
          [serverSideCurrentPage]="bidsCurrentPage"
          [showFilters]="true"
          [showPagination]="true"
          [itemsPerPage]="bidsItemsPerPage"
          [showAcceptButton]="false"
          [showPunksColumn]="false"
          [showStatusColumn]="true"
          (pageChange)="onBidsPageChange($event)"
          (filtersChange)="onBidsFiltersChange($event)">
        </app-stash-bids-table>
      </div>
    </div>

    <div class="external-bids-publications-section" *ngIf="externalBidPublicationsAdminVisible">
      <h3 class="bids-subtitle">External Orderbook Bid Publications</h3>

      <div *ngIf="externalBidPublicationsLoading && externalBidPublicationsTotal === 0" class="loading-state">
        <app-loader></app-loader>
        <p>Loading external bid publications...</p>
      </div>

      <div *ngIf="!externalBidPublicationsLoading && externalBidPublicationsTotal === 0" class="no-graphs">
        <div class="no-graphs-content">
          <h3>No publications</h3>
          <p>No rows found for the current source filter.</p>
        </div>
      </div>

      <div class="external-bids-table-with-loader" *ngIf="externalBidPublicationsTotal > 0">
        <div class="bids-loading-overlay" *ngIf="externalBidPublicationsLoading">
          <app-loader></app-loader>
          <p>Loading page {{ externalBidPublicationsCurrentPage }}...</p>
        </div>

        <div class="bids-table-wrapper">
          <table class="bids-table external-bids-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Source</th>
                <th>Orderbook Bid ID</th>
                <th>Bidder</th>
                <th>Nonce</th>
                <th>Chain</th>
                <th>Amount (wei)</th>
                <th>Tag</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of externalBidPublications">
                <td>{{ item.createdAt | date:'short' }}</td>
                <td>{{ item.source }}</td>
                <td class="mono">{{ item.orderbook_bid_id }}</td>
                <td class="mono">{{ item.bidder_address }}</td>
                <td>{{ item.bid_nonce }}</td>
                <td>{{ item.chain_id }}</td>
                <td class="mono">{{ item.bid_amount_wei }}</td>
                <td>{{ item.tag || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination" *ngIf="externalBidPublicationsTotalPages > 1">
          <button
            class="page-btn"
            type="button"
            (click)="onExternalBidPublicationsPageChange(externalBidPublicationsCurrentPage - 1)"
            [disabled]="externalBidPublicationsCurrentPage <= 1 || externalBidPublicationsLoading"
            [class.disabled]="externalBidPublicationsCurrentPage <= 1 || externalBidPublicationsLoading">
            ‹
          </button>
          <span class="page-number active">
            {{ externalBidPublicationsCurrentPage }}
          </span>
          <span class="page-number">
            {{ externalBidPublicationsTotalPages }}
          </span>
          <button
            class="page-btn"
            type="button"
            (click)="onExternalBidPublicationsPageChange(externalBidPublicationsCurrentPage + 1)"
            [disabled]="
              externalBidPublicationsLoading ||
              (externalBidPublicationsTotalPages > 0 && externalBidPublicationsCurrentPage >= externalBidPublicationsTotalPages)
            "
            [class.disabled]="
              externalBidPublicationsLoading ||
              (externalBidPublicationsTotalPages > 0 && externalBidPublicationsCurrentPage >= externalBidPublicationsTotalPages)
            ">
            ›
          </button>
          <span class="external-bids-count">
            {{ externalBidPublicationsTotal }} rows
          </span>
        </div>
      </div>
    </div>

    <div class="stash-bid-protection-logs-section" *ngIf="stashBidProtectionStatusSectionVisible">
      <h3 class="bids-subtitle">Stash bid protection log</h3>
      <p class="bids-description" *ngIf="stashBidProtectionServerEnabled === false">
        Backend feature is off (<code>ENABLE_STASH_BID_PROTECTION</code>). Enable it on listings-scraper to record and run protection.
      </p>

      <div *ngIf="stashBidProtectionLogsLoading && stashBidProtectionLogsTotal === 0" class="loading-state">
        <app-loader></app-loader>
        <p>Loading protection log...</p>
      </div>

      <div *ngIf="!stashBidProtectionLogsLoading && stashBidProtectionServerEnabled === true && stashBidProtectionLogsTotal === 0" class="no-graphs">
        <div class="no-graphs-content">
          <h3>No protection events</h3>
          <p>No log rows yet (or no matching legacy bid events).</p>
        </div>
      </div>

      <div class="stash-protection-table-with-loader" *ngIf="stashBidProtectionServerEnabled === true && stashBidProtectionLogsTotal > 0">
        <div class="bids-loading-overlay" *ngIf="stashBidProtectionLogsLoading">
          <app-loader></app-loader>
          <p>Loading page {{ stashBidProtectionLogsCurrentPage }}...</p>
        </div>

        <div class="bids-table-wrapper">
          <table class="bids-table stash-protection-logs-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Protected bid id</th>
                <th>Punk</th>
                <th>Legacy bidder</th>
                <th>Value (wei)</th>
                <th>Legacy tx</th>
                <th>Reset tx</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of stashBidProtectionLogs">
                <td>{{ row.created_at | date:'short' }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.reason }}</td>
                <td class="mono">{{ row.protected_bid_id }}</td>
                <td>{{ row.legacy_punk_index }}</td>
                <td class="mono">{{ row.legacy_bidder_address }}</td>
                <td class="mono">{{ row.legacy_value_wei }}</td>
                <td class="mono">
                  <a
                    *ngIf="row.legacy_tx_hash"
                    class="stash-protection-tx-link"
                    [href]="'https://etherscan.io/tx/' + row.legacy_tx_hash"
                    target="_blank"
                    rel="noopener noreferrer">{{ row.legacy_tx_hash | slice:0:10 }}…</a>
                  <span *ngIf="!row.legacy_tx_hash">-</span>
                </td>
                <td class="mono">
                  <a
                    *ngIf="row.reset_tx_hash"
                    class="stash-protection-tx-link"
                    [href]="'https://etherscan.io/tx/' + row.reset_tx_hash"
                    target="_blank"
                    rel="noopener noreferrer">{{ row.reset_tx_hash | slice:0:10 }}…</a>
                  <span *ngIf="!row.reset_tx_hash">-</span>
                </td>
                <td class="stash-protection-logs-table__actions">
                  <button
                    type="button"
                    class="stash-protection-log-detail-btn"
                    (click)="openStashProtectionLogDetailDialog(row); $event.stopPropagation()">
                    Detail
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination" *ngIf="stashBidProtectionLogsTotalPages > 1">
          <button
            class="page-btn"
            type="button"
            (click)="onStashBidProtectionLogsPageChange(stashBidProtectionLogsCurrentPage - 1)"
            [disabled]="stashBidProtectionLogsCurrentPage <= 1 || stashBidProtectionLogsLoading"
            [class.disabled]="stashBidProtectionLogsCurrentPage <= 1 || stashBidProtectionLogsLoading">
            ‹
          </button>
          <span class="page-number active">{{ stashBidProtectionLogsCurrentPage }}</span>
          <span class="page-number">{{ stashBidProtectionLogsTotalPages }}</span>
          <button
            class="page-btn"
            type="button"
            (click)="onStashBidProtectionLogsPageChange(stashBidProtectionLogsCurrentPage + 1)"
            [disabled]="
              stashBidProtectionLogsLoading ||
              (stashBidProtectionLogsTotalPages > 0 && stashBidProtectionLogsCurrentPage >= stashBidProtectionLogsTotalPages)
            "
            [class.disabled]="
              stashBidProtectionLogsLoading ||
              (stashBidProtectionLogsTotalPages > 0 && stashBidProtectionLogsCurrentPage >= stashBidProtectionLogsTotalPages)
            ">
            ›
          </button>
          <span class="stash-protection-count">{{ stashBidProtectionLogsTotal }} rows</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Changelog Section -->
  <div class="changelog-section">
    <div class="section-title">
      <h2>Changelog</h2>
      <p>Track of features and improvements added to the platform</p>
    </div>

    <div class="changelog-list">
      <div class="changelog-item" *ngFor="let entry of filteredChangelog">
        <div class="changelog-date">{{ entry.date }}</div>
        <div class="changelog-features">
          <ul>
            <li *ngFor="let feature of entry.features">{{ feature }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal-overlay stash-protection-log-detail-dialog"
    [class.active]="stashProtectionLogDetailRow !== null"
    (click)="closeStashProtectionLogDetailDialog()"
    role="presentation">
    <div class="modal stash-protection-log-detail-dialog__panel" (click)="$event.stopPropagation()" *ngIf="stashProtectionLogDetailRow as d">
      <button
        type="button"
        class="close-modal stash-protection-log-detail-dialog__close-x"
        (click)="closeStashProtectionLogDetailDialog()"
        aria-label="Close dialog">
        ×
      </button>
      <header class="stash-protection-log-detail-dialog__header">
        <h3 class="stash-protection-log-detail-dialog__title">Protection log</h3>
        <p class="stash-protection-log-detail-dialog__subtitle">Legacy bid overlap and reset attempt</p>
      </header>

      <section class="stash-protection-log-detail-dialog__section" aria-label="Summary">
        <h4 class="stash-protection-log-detail-dialog__section-label">Summary</h4>
        <div class="stash-protection-log-detail-dialog__meta">
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Status</span>
            <span class="stash-protection-log-detail-dialog__meta-val">
              <span class="stash-protection-log-detail-dialog__status-pill">{{ d.status }}</span>
            </span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Reason</span>
            <span class="stash-protection-log-detail-dialog__meta-val">{{ d.reason }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Punk</span>
            <span class="stash-protection-log-detail-dialog__meta-val">#{{ d.legacy_punk_index }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Protected bid</span>
            <span class="stash-protection-log-detail-dialog__meta-val mono">{{ d.protected_bid_id }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Created</span>
            <span class="stash-protection-log-detail-dialog__meta-val">{{ d.created_at | date:'medium' }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row" *ngIf="d.protected_source">
            <span class="stash-protection-log-detail-dialog__meta-key">Protected source</span>
            <span class="stash-protection-log-detail-dialog__meta-val">{{ d.protected_source }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row" *ngIf="d.legacy_block_number != null">
            <span class="stash-protection-log-detail-dialog__meta-key">Legacy block</span>
            <span class="stash-protection-log-detail-dialog__meta-val">{{ d.legacy_block_number }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Legacy log index</span>
            <span class="stash-protection-log-detail-dialog__meta-val">{{ d.legacy_log_index }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Legacy bidder</span>
            <span class="stash-protection-log-detail-dialog__meta-val mono">{{ d.legacy_bidder_address }}</span>
          </div>
          <div class="stash-protection-log-detail-dialog__meta-row">
            <span class="stash-protection-log-detail-dialog__meta-key">Value (wei)</span>
            <span class="stash-protection-log-detail-dialog__meta-val mono">{{ d.legacy_value_wei }}</span>
          </div>
        </div>
      </section>

      <section class="stash-protection-log-detail-dialog__section" aria-label="Error details">
        <h4 class="stash-protection-log-detail-dialog__section-label">Error / notes</h4>
        <pre class="stash-protection-log-detail-dialog__error-body">{{ d.error_message || '—' }}</pre>
      </section>

      <section class="stash-protection-log-detail-dialog__section stash-protection-log-detail-dialog__section--links" *ngIf="d.legacy_tx_hash || d.reset_tx_hash">
        <h4 class="stash-protection-log-detail-dialog__section-label">On-chain</h4>
        <div class="stash-protection-log-detail-dialog__links">
          <a
            *ngIf="d.legacy_tx_hash"
            class="stash-protection-log-detail-dialog__link"
            [href]="'https://etherscan.io/tx/' + d.legacy_tx_hash"
            target="_blank"
            rel="noopener noreferrer">Legacy transaction</a>
          <a
            *ngIf="d.reset_tx_hash"
            class="stash-protection-log-detail-dialog__link"
            [href]="'https://etherscan.io/tx/' + d.reset_tx_hash"
            target="_blank"
            rel="noopener noreferrer">Reset transaction</a>
        </div>
      </section>

      <footer class="stash-protection-log-detail-dialog__footer">
        <button type="button" class="stash-protection-log-detail-dialog__close-btn" (click)="closeStashProtectionLogDetailDialog()">
          Close
        </button>
      </footer>
    </div>
  </div>
</div>
